import React from 'react';
import { BlogLayout, H2, H3, P, UL, InfoBox, ScoreCard } from './BlogLayout';

const Blog3 = () => (
  <BlogLayout
    title="SSL Certificate Explained for Beginners (India)"
    description="What is an SSL certificate, why does the padlock matter, and what happens when it expires? Everything Indian website owners need to know."
    date="April 2026"
    readTime={6}
    category="Beginner Guide"
  >
    <P>
      If you've ever noticed a padlock next to a website's address in your browser, you've seen SSL in action. But what exactly is an SSL certificate? Why do you need one? And what happens if it expires?
    </P>
    <P>
      This guide answers all of that in plain English — no technical background needed.
    </P>

    <H2>What Is an SSL Certificate?</H2>
    <P>
      SSL stands for Secure Sockets Layer (though modern versions use TLS — Transport Layer Security). An SSL certificate is a small digital file installed on your web server that does two things:
    </P>
    <UL items={[
      "Encrypts the connection between your visitor's browser and your server — so no one in the middle can read the data",
      'Verifies your identity — it proves that the website visitors are connecting to is actually yours and not an impersonation',
    ]} />
    <InfoBox title="Simple analogy" color="#00F0FF">
      Think of it like sending a letter. Without SSL, your letter travels in a transparent envelope — anyone who handles it can read it. With SSL, the letter is in a sealed, tamper-proof box that only the intended recipient can open.
    </InfoBox>

    <H2>Why Does the Padlock Matter?</H2>
    <P>
      The padlock icon in your browser's address bar tells visitors that the connection to the website is encrypted and verified. When they see it, they know:
    </P>
    <UL items={[
      'Their data (passwords, payment details, form submissions) is encrypted in transit',
      "The website has been verified to own the domain they're visiting",
      "They're not on a fake copy of the site created by an attacker",
    ]} />
    <P>
      Without the padlock, modern browsers like Chrome and Firefox show a "Not Secure" warning. Many visitors will immediately leave when they see this — especially on payment or login pages.
    </P>
    <ScoreCard label="🔒 Padlock shown" score="Site is secure — visitors trust it" color="#00FF94" />
    <ScoreCard label="⚠️ Not Secure warning" score="Visitors see a warning before entering data" color="#FFD600" />
    <ScoreCard label="🔴 Red warning page" score="Certificate is invalid or expired — site blocked" color="#FF0055" />

    <H2>Types of SSL Certificates</H2>
    <H3>Domain Validation (DV) — Most common</H3>
    <P>
      Verifies that you own the domain. Issued in minutes. This is what Let's Encrypt provides for free and is perfectly sufficient for most websites — blogs, business sites, SaaS tools.
    </P>
    <H3>Organisation Validation (OV)</H3>
    <P>
      Verifies your organisation's legal existence in addition to domain ownership. Takes 1–3 days. Suitable for business websites where trust is important.
    </P>
    <H3>Extended Validation (EV)</H3>
    <P>
      The most rigorous — requires legal, physical, and operational verification of your business. Previously showed a green address bar but modern browsers have removed that visual distinction. Still used by banks and major financial institutions.
    </P>

    <H2>Is SSL Free in India?</H2>
    <P>
      Yes — for most websites. <strong style={{ color: '#00FF94' }}>Let's Encrypt</strong> provides free, automatically renewing SSL certificates that are trusted by all major browsers. Most Indian hosting providers (Hostinger India, BigRock, SiteGround) offer Let's Encrypt integration with one click.
    </P>
    <InfoBox title="Where to get a free SSL certificate" color="#00FF94">
      <strong>Let's Encrypt</strong> — Free, auto-renews every 90 days, trusted everywhere. Your hosting panel likely has a "Let's Encrypt" button under SSL settings. If not, ask your hosting provider to enable it.
    </InfoBox>
    <P>
      Paid SSL certificates from DigiCert, Comodo, or GlobalSign are only needed if you specifically require OV or EV validation — most businesses don't.
    </P>

    <H2>What Happens When an SSL Certificate Expires?</H2>
    <P>
      This is where many website owners get caught out. SSL certificates expire — free Let's Encrypt certificates expire every 90 days (auto-renewal usually handles this), while paid ones expire annually.
    </P>
    <P>When a certificate expires:</P>
    <UL items={[
      'Chrome shows a full-page red warning: "Your connection is not private"',
      "Visitors have to click through a scary warning to access your site — most won't",
      'Google may temporarily demote your site in search rankings',
      'Any payment integrations that check SSL validity will stop working',
      "Email services that verify your domain's SSL may flag outgoing emails as suspicious",
    ]} />
    <InfoBox title="Set a calendar reminder" color="#FF0055">
      If you're on a paid certificate, note the expiry date and set a reminder 30 days before. For Let's Encrypt, check that auto-renewal is working by running a scan — WebShield's SSL check shows your exact expiry date.
    </InfoBox>

    <H2>How to Check Your SSL Certificate</H2>
    <H3>In the Browser</H3>
    <P>
      Click the padlock icon → Connection is secure → Certificate is valid → view certificate details. Check the "Valid until" date.
    </P>
    <H3>Common SSL Problems in India</H3>
    <UL items={[
      'Mixed content: your site loads on HTTPS but some images, scripts, or stylesheets still load over HTTP — breaking the padlock',
      'Wrong domain: certificate is issued for www.yourdomain.com but your site runs on yourdomain.com (or vice versa)',
      'Self-signed certificate: fine for internal tools, but browsers will warn visitors on public sites',
      "Chain not complete: some hosting setups don't include intermediate certificates — some devices will show errors",
    ]} />

    <H2>SSL and SEO in India</H2>
    <P>
      Google confirmed in 2014 that HTTPS is a ranking signal. Since then, the weight given to HTTPS in rankings has only increased. Practically, this means:
    </P>
    <UL items={[
      'A site without SSL will rank lower than an equivalent site with SSL',
      "Google Search Console shows HTTPS and HTTP as separate properties — make sure you're tracking the HTTPS version",
      'Any backlinks pointing to your HTTP version should ideally redirect to HTTPS',
    ]} />
    <P>
      For Indian businesses targeting local search results on Google.co.in, having a valid SSL certificate is a baseline requirement — not a nice-to-have.
    </P>
  </BlogLayout>
);

export default Blog3;